import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout, PageSection } from "@/components/site/PageLayout";
import { Reveal, Label } from "@/components/site/Reveal";
import { ldScript, serviceSchema } from "@/lib/schema";
import heroImg from "@/assets/hero-verification.jpg";

export const Route = createFileRoute("/verification-letters-inspections")({
  head: () => ({
    ...pageHead({
      title: "Verification Letters & Inspections · SunSmart Engineering",
      description:
        "Field verification, site inspections and engineering letters that keep solar and construction projects moving through jurisdictional approval nationwide.",
      path: "/verification-letters-inspections",
    }),
    scripts: [
      ldScript(
        serviceSchema({
          name: "Verification Letters & Inspections",
          description:
            "Field verification, site inspections, and engineering letters to keep solar and construction projects moving through jurisdictional approval.",
          url: "/verification-letters-inspections",
          serviceType: "Inspection Services",
        }),
      ),
    ],
  }),
  component: VerificationLettersPage,
});

const whatWeDo = [
  {
    n: "01",
    title: "Site Inspections",
    desc: "Conduct thorough inspections of job sites to evaluate work quality, adherence to codes, and potential issues.",
  },
  {
    n: "02",
    title: "Photo Evaluations",
    desc: "Review high-resolution photos of installations or completed projects to provide expert analysis.",
  },
  {
    n: "03",
    title: "Detailed Reporting",
    desc: "Create professional, comprehensive letters outlining our findings for submission to insurance companies, property owners, or other stakeholders.",
  },
];

const process = [
  { n: "01", k: "Initial Consultation", v: "Discuss your inspection needs with our team to identify the scope and purpose of the evaluation, whether it's for insurance claims, project validation, or dispute resolution." },
  { n: "02", k: "Document Submission or Site Visit", v: "For photo evaluations, submit high-quality images and any relevant documentation. For on-site inspections, schedule a visit with one of our experienced professionals." },
  { n: "03", k: "Evaluation Process", v: "Our experts will meticulously review the provided materials or job site, noting any issues, compliance concerns, or areas of excellence." },
  { n: "04", k: "Comprehensive Reporting", v: "Receive a detailed, easy-to-understand report summarizing our findings, including code compliance analysis, structural assessments, and recommendations for repairs or improvements." },
  { n: "05", k: "Follow-Up Support", v: "We're available to answer questions, provide clarification, and assist in implementing any recommendations outlined in our reports." },
];

const commitments = [
  { title: "Impartial", desc: "We provide unbiased, fact-based assessments." },
  { title: "Accurate", desc: "Our reports are rooted in a detailed understanding of industry standards and regulations." },
  { title: "Professional", desc: "Each evaluation is documented clearly and professionally, ready for submission to the appropriate parties." },
];

function VerificationLettersPage() {
  return (
    <PageLayout
      kicker="Construction Services · Verification"
      title="Verification Letters & Inspections"
      intro="We offer professional inspection and evaluation services tailored to meet the needs of insurance companies, contractors, and private individuals. Whether it's an on-site inspection or a review of submitted photos, our objective evaluations provide third-party verification to help you achieve your desired results, without compromising on accuracy or integrity."
    >
      {/* WHAT WE DO */}
      <PageSection label="01" title="What We Do">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <Reveal>
            <div className="h-full overflow-hidden rounded-sm border border-border">
              <img
                src={heroImg}
                alt="Solar installation under third-party inspection and verification"
                loading="lazy"
                className="aspect-[4/3] h-full w-full object-cover"
              />
            </div>
          </Reveal>
          {whatWeDo.map((w, i) => (
            <Reveal key={w.title} delay={(i + 1) * 100}>
              <div className="flex h-full flex-col rounded-sm border border-border bg-surface p-8">
                <div className="font-display text-4xl font-black leading-none text-sun">{w.n}</div>
                <div className="mt-4 font-display text-lg font-bold uppercase">{w.title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </PageSection>

      {/* HOW IT WORKS */}
      <PageSection label="02" title="How It Works" theme="surface">
        <div className="grid gap-4">
          {process.map((s, i) => (
            <Reveal key={s.n} delay={i * 90}>
              <div className="flex gap-6 rounded-sm border border-border bg-background p-6">
                <div className="font-display text-4xl font-black leading-none text-sun">{s.n}</div>
                <div>
                  <div className="font-display text-lg font-bold uppercase leading-tight">{s.k}</div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{s.v}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </PageSection>

      {/* COMMITMENT TO TRUTH AND TRANSPARENCY */}
      <PageSection label="03" title="Our Commitment to Truth & Transparency">
        <Reveal>
          <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground">
            We understand the importance of third-party verification in achieving desired
            outcomes, but we uphold a strict commitment to honesty and accuracy. While we aim to
            support our clients' objectives, we will never fabricate or alter findings to
            misrepresent the truth.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-px border border-border bg-border sm:grid-cols-3">
          {commitments.map((c, i) => (
            <Reveal key={c.title} delay={i * 100}>
              <div className="h-full bg-background p-8">
                <div className="font-display text-lg font-bold uppercase text-sun">{c.title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </PageSection>

      {/* GET STARTED TODAY */}
      <PageSection label="04" title="Get Started Today">
        <div className="grid gap-16 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-8">
            <p className="text-lg leading-relaxed text-muted-foreground">
              Contact us to schedule your inspection or submit your photos for evaluation.
              SunSmart Engineering is here to provide the third-party verification you need with
              the professionalism and honesty you can rely on.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
              >
                Get started <span>→</span>
              </Link>
            </div>
          </Reveal>
        </div>

        {/* related services */}
        <div className="mt-16 border-t border-border pt-10">
          <Label n="→" text="Related Services" />
          <div className="mt-6 flex flex-wrap gap-4">
            <Link
              to="/roof-letters"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              Roof Letters
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              All Services
            </Link>
            <Link
              to="/pv"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              Solar Electric (PV) Design
            </Link>
            <Link
              to="/energy-storage"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              Energy Storage Design
            </Link>
          </div>
        </div>
      </PageSection>
    </PageLayout>
  );
}
